import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { date, z } from 'zod';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from '@radix-ui/react-icons';
import { addDays, format, startOfWeek, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { OrganizationService, OrgStripeProduct } from '@/api';
import { syncInvoicesFromStripe } from '@/stores/accountreceivableStore';



interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define the form schema
const formSchema = z.object({
  syncDate: z.object({
    from: z.date(),
    to: z.date(),
  }),
  syncVoluntaryChurnedSubscriptions: z.boolean().optional(),
  syncInvoluntaryChurnedSubscriptions: z.boolean().optional(),
  selectedProducts: z.array(z.string()).min(1, "At least one product must be selected"),
});

type FormValues = z.infer<typeof formSchema>;

export function StripeSyncDrawer({ open, setOpen }: Props) {
    const [date, setDate] = useState<DateRange | undefined>({
      from: addDays(new Date(), -7),
      to: new Date(),
    });
    const queryClient = useQueryClient();
    const org = useStore($org);
    const po = useStore($portfolio);
    const [orgStripeProducts, setOrgStripeProducts] = useState<OrgStripeProduct[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

    useEffect(() => {
      const fetchStripeProducts = async () => {
        if (org?.id) {
          const products = await OrganizationService.getStripeProducts(org.id);
          setOrgStripeProducts(products);
          // Initialize with no products selected
          setSelectedProducts([]);
          form.setValue('selectedProducts', []);
        }
      };
      
      fetchStripeProducts();
    }, [org?.id]);

    const form = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        syncDate: {
          from: date?.from!,
          to: date?.to!,
        },
        syncVoluntaryChurnedSubscriptions: false,
        syncInvoluntaryChurnedSubscriptions: false,
        selectedProducts: [], // Start with no products selected
      },
    });


  async function onSubmit(data: FormValues) {
    try {
      toast.info('Starting sync...');
      setOpen(false);
      const response = await syncInvoicesFromStripe(
        data.syncDate.from,
        data.syncDate.to,
        po?.portfolio.id ?? '',
        org?.id ?? '',
        data.syncVoluntaryChurnedSubscriptions ?? false,
        data.syncInvoluntaryChurnedSubscriptions ?? false,
        selectedProducts ?? []
      );        
      if (response.succeeded) {
        toast.success('Sync successful');
      } else {
        toast.error('Failed to start sync');
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error('Failed to start sync');
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Sync from Stripe</SheetTitle>
          <SheetDescription>Sync your subscriptions from Stripe. The corresponding dates is for the cancellation date of the subscription.</SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="syncDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Date Range</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-range"
                        variant="outline"
                        className="w-full justify-start test-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date?.from ? (
                          date?.to ? (
                            <>
                              {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
                            </>
                          ) : (
                            format(date.from, 'LLL dd, y')
                          )
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="flex w-auto p-0" align="start">
                      <div className="flex w-[200px] flex-col gap-2 pb-4 pl-4 pr-1 pt-4">
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: addDays(new Date(), -7), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          Last 7 Days
                        </Button>
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: addDays(new Date(), -30), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          Last 30 Days
                        </Button>
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: startOfWeek(new Date()), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          This week
                        </Button>
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: startOfMonth(new Date()), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          This month
                        </Button>
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: startOfQuarter(new Date()), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          This quarter
                        </Button>
                        <Button variant="outline" onClick={() => {
                          const newDate = {from: startOfYear(new Date()), to: new Date()};
                          setDate(newDate);
                          form.setValue('syncDate', newDate);
                        }}>
                          This year
                        </Button>
                      </div>
                      <div className="">
                        <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={date?.to}
                          selected={date}
                          onSelect={(range) => {
                            setDate(range);
                            if (range?.from && range?.to) {
                              form.setValue('syncDate', range as any);
                            }
                          }}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Subscriptions linked to these products will be synced</h3>
              <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                {orgStripeProducts.map((product) => (
                  <div key={product.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={`product-${product.id}`}
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newSelected = [...selectedProducts, product.id];
                          setSelectedProducts(newSelected);
                          form.setValue('selectedProducts', newSelected);
                        } else {
                          const newSelected = selectedProducts.filter(id => id !== product.id);
                          setSelectedProducts(newSelected);
                          form.setValue('selectedProducts', newSelected);
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label 
                      htmlFor={`product-${product.id}`}
                      className="text-sm font-medium text-gray-700"
                    >
                      {product.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <FormField
              control={form.control}
              name="syncVoluntaryChurnedSubscriptions"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">Sync Voluntary Churned Subscriptions</FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="syncInvoluntaryChurnedSubscriptions"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">Sync Involuntary Churned Subscriptions</FormLabel>
                </FormItem>
              )}
            />
            <SheetFooter className="flex flex-col gap-2">
              <Button type="submit">Start Sync</Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}